import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { LucideAngularModule, Delete, Fingerprint, Loader2 } from 'lucide-angular';

export type VariantePinPad = 'sombre' | 'clair';

/** Les classes de chaque element, par variante : `sombre` (fond indigo : verrou, connexion) et `clair` (reglages). */
const CLASSES = {
  sombre: {
    pointPlein: 'bg-primary-400 scale-110 shadow-lg shadow-primary-400/50',
    pointVide: 'bg-white/20',
    touche:
      'h-16 rounded-2xl bg-white/10 backdrop-blur text-white text-2xl font-light hover:bg-white/20 active:scale-95 transition-all duration-150 border border-white/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
    suppression: 'h-16 rounded-2xl text-white/40 hover:bg-white/10 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed',
    biometrie:
      'h-16 rounded-2xl bg-primary-500/30 border border-primary-300/40 text-primary-200 hover:bg-primary-500/40 active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed',
    chargement: 'text-white/60',
    erreur: 'text-danger-400',
  },
  clair: {
    pointPlein: 'bg-primary-600 border-2 border-primary-600 scale-110',
    pointVide: 'border-2 border-surface-300 dark:border-surface-600',
    touche:
      'h-16 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-2xl font-bold text-surface-800 dark:text-white hover:bg-surface-50 dark:hover:bg-surface-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
    suppression:
      'h-16 rounded-xl bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-600 active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed',
    biometrie: '',
    chargement: 'text-surface-500',
    erreur: 'text-danger-500',
  },
} as const;

/**
 * Clavier de NIP : points de saisie, pave numerique, suppression.
 *
 * Presentationnel : il signale chaque appui, c'est l'ecran qui garde le NIP en cours. Meme dessin que
 * la vue « NIP » de la connexion (points, pave en verre depoli, touche de suppression) en variante
 * `sombre` ; la variante `clair` est celle des reglages.
 *
 * `biometrie` met une touche d'empreinte dans la case vide a gauche du 0 (variante `sombre` seulement) :
 * la mise en page ne change pas, que la biometrie soit proposee ou non.
 */
@Component({
  selector: 'app-pin-pad',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule],
  template: `
    <div class="flex justify-center mb-8" [class]="variante() === 'sombre' ? 'gap-4' : 'gap-3'" role="img" [attr.aria-label]="saisis() + ' / ' + longueur()">
      @for (i of points(); track i) {
        <div class="w-4 h-4 rounded-full transition-all duration-200" [class]="i < saisis() ? c().pointPlein : c().pointVide"></div>
      }
    </div>

    @if (charge()) {
      <div class="flex justify-center mb-4"><lucide-icon [img]="Loader2" [size]="20" class="animate-spin" [class]="c().chargement" aria-hidden="true"></lucide-icon></div>
    }

    @if (erreur() && !charge()) {
      <p class="text-center text-sm mb-4" [class]="c().erreur" role="alert">{{ erreur() }}</p>
    }

    <div class="grid grid-cols-3 gap-3 mb-4">
      @for (n of chiffres; track n) {
        <button type="button" (click)="chiffre.emit(n)" [disabled]="charge()" [attr.aria-label]="n" [class]="c().touche">{{ n }}</button>
      }
      @if (biometrie() && variante() === 'sombre') {
        <button type="button" (click)="biometrieDemandee.emit()" [disabled]="charge()" [attr.aria-label]="libelleBiometrie()" [class]="c().biometrie">
          <lucide-icon [img]="Fingerprint" [size]="30" [strokeWidth]="1.6" aria-hidden="true"></lucide-icon>
        </button>
      } @else {
        <span aria-hidden="true"></span>
      }
      <button type="button" (click)="chiffre.emit('0')" [disabled]="charge()" aria-label="0" [class]="c().touche">0</button>
      <button type="button" (click)="supprimer.emit()" [disabled]="charge()" [attr.aria-label]="libelleSupprimer()" [class]="c().suppression">
        <lucide-icon [img]="Delete" [size]="20" aria-hidden="true"></lucide-icon>
      </button>
    </div>
  `,
})
export class PinPadComponent {
  /** Nombre de chiffres du NIP. */
  readonly longueur = input(6);
  /** Combien de chiffres sont deja saisis. */
  readonly saisis = input(0);
  readonly charge = input(false);
  readonly erreur = input<string | null>(null);
  readonly libelleSupprimer = input('⌫');
  readonly variante = input<VariantePinPad>('sombre');
  /** Propose la touche d'empreinte (variante `sombre`). */
  readonly biometrie = input(false);
  readonly libelleBiometrie = input('Biométrie');

  readonly chiffre = output<string>();
  readonly supprimer = output<void>();
  readonly biometrieDemandee = output<void>();

  readonly Delete = Delete;
  readonly Fingerprint = Fingerprint;
  readonly Loader2 = Loader2;
  readonly chiffres = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
  readonly points = computed(() => Array.from({ length: this.longueur() }, (_, i) => i));
  protected readonly c = computed(() => CLASSES[this.variante()]);
}
